import { useState, useMemo, useEffect } from "react";


// @mui material components
import { Box, Container, Grid, Typography, TextField, Button } from "@mui/material";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Images
import loginImage from "assets/images/login.png";
import fitifyLogo from "assets/images/nFitifyLogo.svg";


import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { validateEmail  } from 'utils/common'
import { userSelector, clearState, forgotPassUser } from 'store/features/authSlice';
import { useNavigate } from "react-router-dom";


function ForgotPassword() {

    const dispatch =  useDispatch()
    const { isSuccess, isError, isFetching, message } = useSelector(userSelector);

    useEffect(()=>{
        if(isError){
            toast.error(message)
            dispatch(clearState())
        }

        if(isSuccess){
            toast.success(message)
            dispatch(clearState())
        }

    },[isError, message, dispatch, isSuccess])

    //default fields value
    const defaultHandler = useMemo(() => {
        return {
            email: null,
        }}, []);

    
    //default error fields value
    const errorDefault = useMemo(() => {
        return {
            email: false,
        }}, []);


    const [ formdata, setFormdata ] = useState(defaultHandler)
    const [ errorMessage, setErrorMessage ] =  useState(errorDefault)
    
  //for registering
    const submitHandler = () => {
        if(formdata.email){
            if(!validateEmail(formdata.email)){
                toast.info("Enter a valid email")
                setErrorMessage({
                    ...errorMessage,
                    'email': true
                })
                return;
            }
        } else {
            toast.info("Plz enter email")
            setErrorMessage({
                ...errorMessage,
                'email': true
            })
            return;
        }

        dispatch(forgotPassUser({ formdata }))
    }
    const navigate = useNavigate()


    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#1A1A1A",
                display: "flex",
                alignItems: "center",
                fontFamily: "Outfit",
            }}
        >
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Grid container spacing={4} alignItems="stretch">
                    {/* Left Side - Image */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                height: { xs: "400px", md: "600px" },
                                borderRadius: "20px",
                                overflow: "hidden",
                                position: "relative",
                            }}
                        >
                            <Box
                                component="img"
                                src={loginImage}
                                alt="Forgot Password"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Right Side - Forgot Password Form */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                borderRadius: "20px",
                                padding: { xs: 3, sm: 4, md: 5 },
                                textAlign: "center",
                                height: { xs: "400px", md: "600px" },
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            {/* Logo */}
                            <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Box
                                    component="img"
                                    src={fitifyLogo}
                                    alt="Fitify Logo"
                                    onClick={() => navigate("/")}
                                    sx={{
                                        height: "60px",
                                        width: "auto",
                                        maxWidth: "200px",
                                        cursor: "pointer",
                                        transition: "transform 0.2s ease",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Title */}
                            <Typography
                                variant="h4"
                                sx={{
                                    color: "#17C1E8",
                                    fontWeight: "bold",
                                    mb: 1,
                                    fontFamily: "Outfit",
                                }}
                            >
                                Forgot Password?
                            </Typography>

                            {/* Description */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#FFFFFF",
                                    mb: 4,
                                    fontFamily: "Outfit",
                                    opacity: 0.8,
                                }}
                            >
                                Provide your registered email address, we will send the new password on your registered email address.
                            </Typography>

                            {/* Forgot Password Form */}
                            <Box component="form" sx={{ textAlign: "left" }}>
                                {/* Email Field */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#FFFFFF",
                                            mb: 1,
                                            fontWeight: "bold",
                                            fontFamily: "Outfit",
                                        }}
                                    >
                                        Email*
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="email"
                                        placeholder="Enter your email"
                                        value={formdata?.email ?? ""}
                                        error={errorMessage?.email}
                                        onChange={(e) => {
                                            setErrorMessage({
                                                ...errorMessage,
                                                email: false,
                                            });
                                            setFormdata({
                                                ...formdata,
                                                email: e.target.value,
                                            });
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#FFFFFF",
                                                borderRadius: "10px",
                                                "& fieldset": {
                                                    border: "none",
                                                },
                                                "&:hover fieldset": {
                                                    border: "none",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    border: "2px solid #17C1E8",
                                                },
                                            },
                                            "& .MuiInputBase-input": {
                                                fontFamily: "Outfit",
                                                padding: "12px 16px",
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Submit Button */}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={isFetching}
                                    onClick={submitHandler}
                                    sx={{
                                        backgroundColor: "#17C1E8",
                                        color: "#000000",
                                        fontWeight: "bold",
                                        padding: "12px",
                                        borderRadius: "10px",
                                        fontSize: "16px",
                                        fontFamily: "Outfit",
                                        textTransform: "none",
                                        mb: 3,
                                        "&:hover": {
                                            backgroundColor: "#14A8C4",
                                        },
                                        "&:disabled": {
                                            backgroundColor: "#666",
                                            color: "#999",
                                        },
                                    }}
                                >
                                    {isFetching ? "Submitting..." : "Submit"}
                                </Button>

                                {/* Back to Login Link */}
                                <Box sx={{ textAlign: "center" }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#FFFFFF",
                                            fontFamily: "Outfit",
                                        }}
                                    >
                                        Remember your password?{" "}
                                        <Typography
                                            component="span"
                                            onClick={() => navigate("/login")}
                                            sx={{
                                                color: "#17C1E8",
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                fontFamily: "Outfit",
                                                textDecoration: "none",
                                                "&:hover": {
                                                    textDecoration: "underline",
                                                },
                                            }}
                                        >
                                            Back to login
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default ForgotPassword;